'use client';

import { TextareaHTMLAttributes, forwardRef, useState, useCallback, useRef, useEffect } from 'react';
import styles from './Textarea.module.css';

type TextareaVariant = 'autoGrow' | 'fixedHeight';

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  variant?: TextareaVariant;
  /**
   * Drops the field's own frame so the textarea sits flush in whatever holds it —
   * for a panel card that is already a container, a second box inside it is just
   * a box in a box. The frame comes back on hover and focus, so the field is still
   * findable once you reach for it.
   */
  bare?: boolean;
  label?: string;
  helperText?: string;
  error?: boolean;
  errorText?: string;
  maxLength?: number;
  showCharCount?: boolean;
  noResize?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  variant = 'fixedHeight',
  bare = false,
  label,
  helperText,
  error = false,
  errorText,
  maxLength,
  showCharCount = false,
  noResize = false,
  disabled = false,
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  rows = 4,
  className = '',
  ...props
}, ref) => {
  const [focused, setFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue?.toString() || '');
  const [jitter, setJitter] = useState(false);
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const prevErrorRef = useRef(false);

  const currentValue = value !== undefined ? value.toString() : internalValue;
  const displayHelperText = error && errorText ? errorText : helperText;

  useEffect(() => {
    if (error && !prevErrorRef.current) {
      setJitter(true);
      const timer = setTimeout(() => setJitter(false), 500);
      return () => clearTimeout(timer);
    }
    prevErrorRef.current = error;
  }, [error]);

  const autoResize = useCallback(() => {
    if (variant !== 'autoGrow') return;
    const el = internalRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, [variant]);

  useEffect(() => {
    autoResize();
  }, [currentValue, autoResize]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (value === undefined) {
      setInternalValue(e.target.value);
    }
    onChange?.(e);
  }, [onChange, value]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
    setFocused(false);
    onBlur?.(e);
  }, [onBlur]);

  const setRefs = useCallback((el: HTMLTextAreaElement | null) => {
    internalRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = el;
  }, [ref]);

  const wrapperClasses = [
    styles.wrapper,
    styles[variant],
    bare ? styles.bare : '',
    focused ? styles.focused : '',
    error ? styles.error : '',
    disabled ? styles.disabled : '',
    label ? styles.hasLabel : '',
    noResize ? styles.noResize : '',
    jitter ? styles.jitter : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={wrapperClasses}>
      {label && (
        <span className={styles.label}>{label}</span>
      )}

      <div className={styles.field}>
        <textarea
          ref={setRefs}
          className={styles.textarea}
          disabled={disabled}
          value={value}
          defaultValue={value === undefined ? defaultValue : undefined}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxLength}
          rows={variant === 'autoGrow' ? 1 : rows}
          {...props}
        />
      </div>

      {(displayHelperText || showCharCount) && (
        <div className={styles.footer}>
          {displayHelperText && <span className={styles.helperText}>{displayHelperText}</span>}
          {showCharCount && maxLength && (
            <span className={styles.charCount}>{currentValue.length}/{maxLength}</span>
          )}
        </div>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';
export default Textarea;
